import { useEffect, useRef, useState } from 'react';
import { LAYOUT_PRESETS } from '@cinegrid/core';
import { tabSync } from '../../sync/tabSync';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel } from '../controls/PanelShell';

const PANEL_W = 360;
const PANEL_H = 420;
const STORAGE_KEY = 'cinegrid-window-manager-pos';

type Pos = { x: number; y: number };

function defaultPos(): Pos {
  return {
    x: Math.max(16, window.innerWidth - PANEL_W - 16),
    y: Math.max(16, window.innerHeight - PANEL_H - 16),
  };
}

function loadPos(): Pos {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Pos;
      if (typeof p.x === 'number' && typeof p.y === 'number') return clampPos(p);
    }
  } catch {
    /* ignore */
  }
  return defaultPos();
}

function clampPos(p: Pos): Pos {
  const maxX = Math.max(0, window.innerWidth - PANEL_W);
  const maxY = Math.max(0, window.innerHeight - 48);
  return {
    x: Math.min(maxX, Math.max(0, p.x)),
    y: Math.min(maxY, Math.max(0, p.y)),
  };
}

export function WindowManagerPanel() {
  const layout = useEditorStore((s) => s.layout);
  const setLayout = useEditorStore((s) => s.setLayout);
  const connectedTabs = useEditorStore((s) => s.connectedTabs);
  const tabId = useEditorStore((s) => s.tabId);
  const isLeader = useEditorStore((s) => s.isLeader);
  const setShowWindowManager = useEditorStore((s) => s.setShowWindowManager);

  const [pos, setPos] = useState<Pos>(() =>
    typeof window !== 'undefined' ? loadPos() : { x: 16, y: 16 }
  );
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {
      /* ignore */
    }
  }, [pos]);

  const openNewWindow = (layoutId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('layout', layoutId);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const onHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Don't start a drag from Close / other header buttons
    if ((e.target as HTMLElement).closest('button, a, input')) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
    };

    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      setPos(
        clampPos({
          x: d.originX + (ev.clientX - d.startX),
          y: d.originY + (ev.clientY - d.startY),
        })
      );
    };

    const onUp = (ev: PointerEvent) => {
      if (dragRef.current?.pointerId !== ev.pointerId) return;
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <div
      className="fixed z-50 border border-hairline bg-panel shadow-none"
      style={{ left: pos.x, top: pos.y, width: PANEL_W, height: PANEL_H }}
    >
      <PanelShell
        title="Window Manager"
        headerClassName="cursor-grab active:cursor-grabbing select-none"
        onHeaderPointerDown={onHeaderPointerDown}
        actions={
          <button
            type="button"
            className="text-2xs text-text-low hover:text-text-high"
            onClick={() => setShowWindowManager(false)}
          >
            Close
          </button>
        }
      >
        <SectionLabel>This Window Layout</SectionLabel>
        <div className="space-y-1 p-2">
          {LAYOUT_PRESETS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => {
                setLayout(l);
                tabSync.announceLayout(l);
              }}
              className={`flex w-full items-center justify-between border px-2 py-1.5 text-left text-2xs ${
                layout.id === l.id
                  ? 'border-accent bg-raised text-accent'
                  : 'border-hairline text-text-low hover:text-text-high'
              }`}
            >
              <span>{l.label}</span>
              <span className="text-text-low">{l.panels.length} panels</span>
            </button>
          ))}
        </div>

        <SectionLabel>Open Dual Monitor Tab</SectionLabel>
        <div className="flex flex-wrap gap-1 p-2">
          {LAYOUT_PRESETS.filter((l) => l.id !== 'primary').map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => openNewWindow(l.id)}
              className="border border-hairline bg-raised px-2 py-1.5 text-2xs text-text-high hover:border-accent"
            >
              Open “{l.label}”
            </button>
          ))}
        </div>

        <SectionLabel>Connected Tabs</SectionLabel>
        <ul className="divide-y divide-hairline">
          {connectedTabs.map((t) => (
            <li key={t.tabId} className="flex items-center justify-between px-3 py-2 text-2xs">
              <span className="text-text-high">
                {t.label}
                {t.tabId === tabId ? ' (this)' : ''}
              </span>
              <span className={t.isLeader ? 'text-playhead' : 'text-text-low'}>
                {t.isLeader ? 'leader' : 'follower'}
              </span>
            </li>
          ))}
          {connectedTabs.length === 0 && (
            <li className="p-3 text-2xs text-text-low">Only this tab.</li>
          )}
        </ul>
        <p className="p-3 text-2xs text-text-low">
          Sync uses BroadcastChannel. Leader ({isLeader ? 'you' : 'another tab'}) holds
          authoritative project state. Assign different layouts per tab for dual-monitor editing.
        </p>
      </PanelShell>
    </div>
  );
}
